import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { MODEL } from "../../../model";

/**
 * The SigninForm component.
 *
 * This component renders a form to sign in to the application. It validates
 * the input fields and sends a request to the server to authenticate the user.
 *
 * @returns {JSX.Element} The signin form component.
 */
export default function SigninForm() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ isError: false, text: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });

  const validateInput = (name, value) => {
    const sqlInjectionPattern = /['";-]/;
    if (sqlInjectionPattern.test(value)) {
      return "Invalid characters detected.";
    }
    if (name === "email") {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(value)) {
        return "Invalid email format.";
      }
    }
    if (name === "password") {
      const passwordPattern =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,30}$/;
      if (!passwordPattern.test(value)) {
        return "Password must be 8-30 characters, include uppercase, lowercase, number, and special character.";
      }
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    const error = validateInput(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return; // prevent multiple submissions

    setMessage({ isError: false, text: "Authenticating..." });

    const emailError = validateInput("email", formData.email);
    const passwordError = validateInput("password", formData.password);

    if (emailError || passwordError) {
      setErrors({ email: emailError, password: passwordError });
      return;
    }

    setIsLoading(true); // Start loading
    await authenticateUser(formData);
    setIsLoading(false); // Stop loading
  };

  const authenticateUser = async (data) => {
    try {
      setMessage({ isError: false, text: "Please wait..." });
      const signin_api_url = `${MODEL.api.base_url}/api/auth/signin`;

      const response = await fetch(signin_api_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });

      if (!response.ok) {
        setMessage({ isError: true, text: "Invalid email or password" });
        return;
      }

      const responseData = await response.json();
      // console.log(responseData.token);
      sessionStorage.setItem("token", responseData.token);

      setMessage({ isError: false, text: "Signed in successfully!" });

      navigate("/");
    } catch {
      setMessage({
        isError: true,
        text: "Something went wrong. Please try again later.",
      });
    }
  };

  return (
    <div className="container">
      {/* Background Image */}
      <div
        className="background-image"
        style={{
          backgroundImage: "url('/assets/images/background.webp')",
        }}
      ></div>
      <div className="gradient-overlay"></div>

      <div className="content">
        <div className="welcome-section">
          <h2>Welcome Back</h2>
          <p className="fade">"Sign in to access your dream home."</p>
          <p className="fade delay">"Your journey continues here."</p>
        </div>

        <div className="form-section">
          <div className="form-wrapper">
            <h2>Sign In</h2>
            <form onSubmit={handleSubmit}>
              {message.text && (
                <p
                  className={`message ${message.isError ? "error" : "success"}`}
                >
                  {message.text}
                </p>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    onChange={handleChange}
                    value={formData.email}
                  />
                  <FontAwesomeIcon icon={faEnvelope} className="icon" />
                </div>
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    onChange={handleChange}
                    value={formData.password}
                  />
                  <FontAwesomeIcon icon={faLock} className="icon" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="toggle-password"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && (
                  <p className="error-text">{errors.password}</p>
                )}
              </div>

              <div className="forgot-password">
                <span>Have you</span>
                <Link href="/forgot-password">Forgot Password?</Link>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </button>

              <div className="create-account">
                <span>Click here to </span>
                <Link to="/signup">Create Account</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
